// reducer.ts
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import type {
  AgCommunicationStrategy,
  Company,
  DpeFirstContactTemplateKey,
  DpeGeneralMailTemplateKey,
  ReferentCampaignCustomDocsMap,
  ReferentCampaignTemplateKey,
  ReferentMutation,
} from '../@types/Company';
import type { Fonction } from '../@types/Fonction';
import type { SciMailWindowsMap } from '../@types/SciMail';
import type { User } from '../@types/User';
import { mergeReferentsPreservingPdfIntroduction } from '../utils/branding';

const HIDE_COMPANY_STORAGE_KEY = 'rgpdcopro:hideCompanyName';
const HIDDEN_COMPANY_NAME = 'DEMO CROISSANCE';

const getInitialCompanyNameHidden = (): boolean => {
  if (typeof window === 'undefined') {return false;}
  try {
    return window.localStorage.getItem(HIDE_COMPANY_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistCompanyNameHidden = (value: boolean) => {
  if (typeof window === 'undefined') {return;}
  try {
    window.localStorage.setItem(HIDE_COMPANY_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore persistence errors (private browsing, etc.)
  }
};

const getCookieExpiration = (): number => {
  const ttl = process.env.REACT_APP_JWT_TOKEN_TTL;
  if (ttl) {
    const ttlNumber = parseInt(ttl, 10);
    return isNaN(ttlNumber) ? 1 : ttlNumber / (24 * 60 * 60);
  }
  return 1; // Default to 1 day if REACT_APP_JWT_TOKEN_TTL is not set
};

export interface AuthState {
  token: string | null;
  user: User | null;
  company: Company | null;
  companyOriginalName: string | null;
  companyNameHidden: boolean;
  fonction: Fonction | null;
  availableCompanies: Company[] | null;
  isAdmin: boolean;
  isBackOffice: boolean;
  isAuthReady: boolean;
}

const initialState: AuthState = {
  token: null,
  user: null,
  company: { // init company with default values
    id: null,
    hasPaidAccess: false,
    paidAccessUntil: null,
    name: null,
    siren: null,
    commission: null,
    subcontractors: null,
    subcontractorFor: null,
    realSales: null,
    nbMandates: null,
    nbPreSaleStatements: null,
    pricePreSaleStatements: null,
    nbSaleStatements: null,
    anomalyNotifier: null,
    setupSteps: {
      coproValidation: false,
      addressValidation: false,
      addMissingCopros: false,
      surfaceValidation: false,
      collaboratorAttribution: false,
      mailAlertSetup: false,
      vigilanceAdditionalCopro: false,
      transactionQualification: false,
      reportSettingsComplete: false,
      mailConfigSetup: false,
      sciTargetQualification: false,
      agDateQualification: false,
    },
    details: {
      syndic: false,
      gesLoc: false,
      transa: false,
      loc: false,
      fnaim: false,
      unis: false,
      snpi: false,
      nbLotsHab: null,
      nbLotsHabBurCom: null,
      address: null,
      postalCode: null,
      city: null,
      departement: null,
      fnaimCity: null,
      fnaimPhone: null,
      fnaimWebsite: null,
      fnaimServices: null,
      fnaimAdditionalInfo: null,
      snpiPhone: null,
      snpiOwner: null,
      snpiUrl: null,
      snpiAdministrator: null,
      snpiManager: null,
      snpiHouseAgent: null,
      snpiExpert: null,
      transactionPappers: null,
      gestionImmobilierePappers: null,
      syndicPappers: null,
      garants: null,
      assureurs: null,
      carte: null,
      dateFinCarte: null,
      infoDirigeant: null,
    },
    proabonanoIframe: null,
    transactionQualification: null,
    toolsTimeline: null,
  },
  companyOriginalName: null,
  companyNameHidden: getInitialCompanyNameHidden(),
  fonction: null,
  availableCompanies: null,
  isAdmin: false,
  isBackOffice: false,
  isAuthReady: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<string>) {
      const expirationInDays = getCookieExpiration();
      Cookies.set('jwt', action.payload, { expires: expirationInDays });
      state.token = action.payload;
    },
    resetToken(state) {
      Cookies.remove('jwt');
      state.token = null;
    },
    setUser(state, action: PayloadAction<User>) {
      state.isAdmin = !!action.payload.roles.includes('ROLE_ADMIN');
      state.user = action.payload;
    },
    resetUser(state) {
      state.user = null;
    },
    setSelectedFonction(state, action: PayloadAction<Fonction>) {
      state.fonction = action.payload;
    },
    resetSelectedFonction(state) {
      state.fonction = null;
    },
    setSelectedCompany(state, action: PayloadAction<Company>) {
      const expirationInDays = getCookieExpiration();
      Cookies.set('companySelected', String(action.payload.id), { expires: expirationInDays });
      const company = { ...action.payload };
      // if setupSteps is null, set it to false for all steps
      if (!company.setupSteps) {
        company.setupSteps = {
          coproValidation: false,
          addressValidation: false,
          addMissingCopros: false,
          surfaceValidation: false,
          collaboratorAttribution: false,
          mailAlertSetup: false,
          vigilanceAdditionalCopro: false,
          transactionQualification: false,
          reportSettingsComplete: false,
          mailConfigSetup: false,
        } as Company['setupSteps'];
      }
      
      // Make sure we preserve all brandingSettings data fields in the Redux store
      if (company.brandingSettings && 
          (!company.brandingSettings.referents || !company.brandingSettings.emailSubcontractorName) && 
          state.company?.brandingSettings) {
        company.brandingSettings = {
          ...company.brandingSettings,
          referents: company.brandingSettings.referents || state.company.brandingSettings.referents,
          emailSubcontractorName: company.brandingSettings.emailSubcontractorName || state.company.brandingSettings.emailSubcontractorName,
        };
      }
      
      state.companyOriginalName = company.name ?? null;
      state.company = state.companyNameHidden
        ? ({ ...company, name: HIDDEN_COMPANY_NAME } as Company)
        : company;
    },
    resetSelectedCompany(state) {
      Cookies.remove('companySelected');
      state.company = null;
      state.companyOriginalName = null;
    },
    setAvailableCompanies(state, action: PayloadAction<Company[]>) {
      state.availableCompanies = action.payload;
    },
    resetAvailableCompanies(state) {
      state.availableCompanies = null;
    },
    setCompanyCommission(state, action: PayloadAction<number>) {
      if (state.company) {
        state.company.commission = action.payload;
      }
    },
    setIsBackOffice(state, action: PayloadAction<boolean>) {
      state.isBackOffice = action.payload;
    },
    validateCompanyStep(state, action: PayloadAction<keyof Company['setupSteps']>) {
      if (state.company) {
        state.company.setupSteps[action.payload] = true;
      }
    },
    invalidateCompanyStep(state, action: PayloadAction<keyof Company['setupSteps']>) {
      if (state.company) {
        state.company.setupSteps[action.payload] = false;
      }
    },
    setCompanyBrandingSettings(state, action: PayloadAction<{
      color1: string;
      color2: string;
      color3: string;
      blogStructure: 'straight-list' | 'alternated-list' | 'grid';
      defaultTemplate?: string;
    }>) {
      if (state.company) {
        state.company = {
          ...state.company,
          brandingSettings: action.payload,
        };
      }
    },
    setCompanyBrandingColors(state, action: PayloadAction<{color1: string; color2: string; color3: string;}>) {
      if (state.company) {
        state.company = {
          ...state.company,
          brandingSettings: {
            ...state.company.brandingSettings,
            ...action.payload,
          },
        };
      }
    },
    setCompanyBrandingBlogStructure(state, action: PayloadAction<'straight-list' | 'alternated-list' | 'grid'>) {
      if (state.company) {
        state.company = {
          ...state.company,
          brandingSettings: {
            ...state.company.brandingSettings,
            blogStructure: action.payload,
          },
        };
      }
    },
    updateCompanyBranding(state, action: PayloadAction<{
      color1?: string;
      color2?: string;
      color3?: string;
      blogStructure?: 'straight-list' | 'alternated-list' | 'grid';
      defaultTemplate?: string;
      emailSubcontractorName?: string;
      commercialName?: string;
      showLegalInfoOnBlog?: boolean;
      referents?: ReferentMutation[];
      defaultDpeFirstContactTemplate?: DpeFirstContactTemplateKey;
      defaultReferentCampaign1?: ReferentCampaignTemplateKey;
      defaultReferentCampaign2?: ReferentCampaignTemplateKey;
      defaultReferentCampaign3?: ReferentCampaignTemplateKey;
      referentCampaignEmailText?: string;
      referentCampaignCustomDocs?: ReferentCampaignCustomDocsMap;
      defaultAgCommunicationStrategy?: AgCommunicationStrategy;
      sciMailWindows?: SciMailWindowsMap;
      defaultDpeGeneralMailTemplate?: DpeGeneralMailTemplateKey;
    }>) {
      if (state.company) {
        const payload = { ...action.payload };
        if (payload.referents && state.company.brandingSettings?.referents) {
          payload.referents = mergeReferentsPreservingPdfIntroduction(
            payload.referents,
            state.company.brandingSettings.referents,
          );
        }
        state.company = {
          ...state.company,
          brandingSettings: {
            ...state.company.brandingSettings,
            ...payload,
          },
        };
      }
    },
    updateCompanyImages(state, action: PayloadAction<{
      brandingImageName?: string;
      brandingBlogImageName?: string;
    }>) {
      if (state.company) {
        state.company = {
          ...state.company,
          brandingSettings: {
            ...state.company.brandingSettings,
            ...action.payload,
          },
        };
      }
    },
    updateMandatoryNewsletterSentAt(state, action: PayloadAction<string>) {
      if (state.company) {
        state.company.mandatoryNewsletterSentAt = action.payload;
      }
    },
    setCompanyToolsTimeline(state, action: PayloadAction<Company['toolsTimeline']>) {
      if (state.company) {
        state.company = {
          ...state.company,
          toolsTimeline: action.payload,
        } as Company;
      }
    },
    refreshCompanyData(state, action: PayloadAction<Company>) {
      if (state.company && state.company.id === action.payload.id) {
        // Preserve referents from the store only when the API omits them entirely.
        if (action.payload.brandingSettings && state.company.brandingSettings) {
          const incomingReferents = action.payload.brandingSettings.referents;
          action.payload.brandingSettings = {
            ...action.payload.brandingSettings,
            referents: incomingReferents ?? state.company.brandingSettings.referents,
            emailSubcontractorName: action.payload.brandingSettings.emailSubcontractorName
              || state.company.brandingSettings.emailSubcontractorName,
          };
        }
        // Normalize setupSteps to ensure it's always an object in the store
        const nextCompany: Company = { ...action.payload } as Company;
        if (!nextCompany.setupSteps) {
          nextCompany.setupSteps = {
            coproValidation: false,
            addressValidation: false,
            addMissingCopros: false,
            surfaceValidation: false,
            collaboratorAttribution: false,
            mailAlertSetup: false,
            vigilanceAdditionalCopro: false,
            transactionQualification: false,
            reportSettingsComplete: false,
            mailConfigSetup: false,
          } as Company['setupSteps'];
        }
        state.companyOriginalName = nextCompany.name ?? null;
        state.company = state.companyNameHidden
          ? ({ ...nextCompany, name: HIDDEN_COMPANY_NAME } as Company)
          : nextCompany;
      }
    },
    updateCompanyReportSettings(state, action: PayloadAction<Company['reportSettings']>) {
      if (state.company) {
        state.company = {
          ...state.company,
          reportSettings: action.payload,
        };
      }
    },
    setAuthReady(state, action: PayloadAction<boolean>) {
      state.isAuthReady = action.payload;
    },
    setCompanyNameHidden(state, action: PayloadAction<boolean>) {
      state.companyNameHidden = action.payload;
      persistCompanyNameHidden(action.payload);
      if (state.company) {
        if (!state.companyOriginalName) {
          state.companyOriginalName = state.company.name ?? null;
        }
        const original = state.companyOriginalName ?? state.company.name;
        state.companyOriginalName = original ?? null;
        state.company = {
          ...state.company,
          name: action.payload ? HIDDEN_COMPANY_NAME : original,
        } as Company;
      }
    },
  },
});

export const {
  setToken,
  resetToken,
  setUser,
  resetUser,
  setSelectedFonction,
  resetSelectedFonction,
  setSelectedCompany,
  resetSelectedCompany,
  setCompanyCommission,
  setAvailableCompanies,
  resetAvailableCompanies,
  setIsBackOffice,
  validateCompanyStep,
  invalidateCompanyStep,
  setCompanyBrandingSettings,
  setCompanyBrandingColors,
  setCompanyBrandingBlogStructure,
  updateCompanyBranding,
  updateCompanyImages,
  updateMandatoryNewsletterSentAt,
  setCompanyToolsTimeline,
  refreshCompanyData,
  updateCompanyReportSettings,
  setAuthReady,
  setCompanyNameHidden,
} = authSlice.actions;
export default authSlice.reducer;
